import { Component, Input, OnChanges, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { HistoryService } from 'src/app/api/services/history.service';
import { AuditHistoryDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/audit-history-dto';

import moment from 'moment';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { HttpErrorResponse } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { OkEgisServiceModelsDtoHistoryRemarkCreateDto } from 'src/app/api/models';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AuditHistoryType } from '../../aftale/details/models/selectfiltermodels';
import { EntityOperationEnum } from 'src/app/api/models/OK/Egis/Service/Models/Messages/Enums/entity-operation-enum';
import { AuditHistoryEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/audit-history-enum';
import { AuthService } from 'src/app/services/auth.service';
import { HistoryRemarkUpdateDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/history-remark-update-dto';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { EditHistoryRemarkDialogComponent } from './edit-history-remark-dialog/edit-history-remark-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { EditHistoryRemarkDialogData } from '../../aftale/models';

@Component({
    selector: 'app-history',
    templateUrl: './history.component.html',
    styleUrls: ['./history.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HistoryComponent implements OnInit, OnChanges {
  canWrite: boolean = false;

  @Input() entityId: number;
  @Input() entityType: AuditHistoryEnum;

  _auditHistoryTypeFilter: AuditHistoryType;
  @Input() set auditHistoryTypeFilter(value: AuditHistoryType) {
    this._auditHistoryTypeFilter = value;
    this.filterHistory(this._auditHistoryTypeFilter);
  }

  constructor(
    private historyService: HistoryService,
    private errorHandler: ErrorHandlerService,
    private datePipe: DatePipe,
    private formBuilder: UntypedFormBuilder,
    private auth: AuthService,
    private dialog: MatDialog
  ) {
    this.canWrite = this.auth.canWrite;
  }

  private filterHistory(filter: AuditHistoryType) {
    if (this.historyEntries)
      switch (+filter) {
        case AuditHistoryType.Bemærkninger:
          this.filteredHistoryEntries = this.historyEntries.filter((o) => o.typeOfChange == EntityOperationEnum.Remark);
          break;
        case AuditHistoryType.Systembeskeder:
          this.filteredHistoryEntries = this.historyEntries.filter(
            (o) => o.typeOfChange != EntityOperationEnum.Remark && o.typeOfChange != EntityOperationEnum.Error
          );
          break;
        case AuditHistoryType.Fejl:
          this.filteredHistoryEntries = this.historyEntries.filter((o) => o.typeOfChange == EntityOperationEnum.Error);
          break;
        default:
          this.filteredHistoryEntries = this.historyEntries;
      }
  }

  private historyEntries: AuditHistoryDto[];
  filteredHistoryEntries: AuditHistoryDto[];
  displayedColumns: string[] = ['timestamp', 'responsibleUser', 'typeOfChange', 'action'];

  currentUsername: string = this.auth.username;
  isAdmin: boolean = this.auth.isAdministrator;

  notesForm: UntypedFormGroup;

  get remark() {
    return this.notesForm.get('remark');
  }

  public noWhitespaceValidator(control: UntypedFormControl) {
    const isWhitespace = (control.value || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : { whitespace: true };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.entityId && this.entityType) {
      this.render();
    }
  }

  ngOnInit(): void {
    this.notesForm = this.formBuilder.group({
      remark: [null, [Validators.maxLength(2000), Validators.required, this.noWhitespaceValidator]],
    });
  }

  shouldShowHistoryRemark(): boolean {
    return this.entityType == AuditHistoryEnum.Serviceaftale;
  }

  getEntryTimestamp(entryDate: Date | string) {
    let today = moment();
    let yesterday = moment().subtract(1, 'day');

    let isToday = moment(entryDate).isSame(today, 'day');
    let isYesterday = moment(entryDate).isSame(yesterday, 'day');

    let time = this.datePipe.transform(entryDate, 'HH.mm:ss');

    if (isToday || isYesterday) {
      return (isToday ? 'i dag kl.' : 'i går kl.') + time;
    }
    return this.datePipe.transform(entryDate, 'dd.MM.yyyy') + ' kl.' + time;
  }

  AddRemark() {
    let createDto: OkEgisServiceModelsDtoHistoryRemarkCreateDto = {
      remark: this.remark.value,
      entityId: this.entityId,
    };

    this.historyService.addHistoryRemark({ body: createDto, caller: EGIS_SERVICE_CALLER }).subscribe((data) => {
      this.notesForm.reset();
      this.historyEntries.unshift(data);
    }),
      (error: HttpErrorResponse) => {
        this.errorHandler.HandleError(error);
      };
  }

  DeleteRemark(entry: AuditHistoryDto) {
    this.historyService.deleteRemark({ id: entry.id, caller: EGIS_SERVICE_CALLER }).subscribe((data) => {
      let index = this.historyEntries.indexOf(entry);
      if (index !== -1) {
        this.historyEntries[index] = data;
      }
    }),
      (error: HttpErrorResponse) => {
        this.errorHandler.HandleError(error);
      };
  }

  EditRemark(entry: AuditHistoryDto) {
    let data: EditHistoryRemarkDialogData = {
      currentRemark: entry.details,
    };
    let dialogRef = this.dialog.open(EditHistoryRemarkDialogComponent, {
      data: data,
    });

    dialogRef.afterClosed().subscribe((newRemark) => {
      if (newRemark) {
        let updateDto: HistoryRemarkUpdateDto = {
          id: entry.id,
          remark: newRemark,
        };
        this.historyService.editRemark({ body: updateDto, caller: EGIS_SERVICE_CALLER }).subscribe((data) => {
          let index = this.historyEntries.indexOf(entry);
          if (index !== -1) {
            this.historyEntries[index] = data;
          }
        }),
          (error: HttpErrorResponse) => {
            this.errorHandler.HandleError(error);
          };
      }
    });
  }

  render() {
    this.historyEntries = [];
    this.filteredHistoryEntries = [];
    this.historyService
      .getAuditHistoryForEntity({ id: this.entityId, auditHistoryEnum: this.entityType, caller: EGIS_SERVICE_CALLER })
      .subscribe((data) => {
        this.historyEntries = data;
        this.filteredHistoryEntries = data;
        this.filterHistory(this._auditHistoryTypeFilter);
      }),
      (error: HttpErrorResponse) => {
        this.errorHandler.HandleError(error);
      };
  }

  isSystem(historyEntry: AuditHistoryDto) {
    return historyEntry.responsibleUser == 'N/A' || historyEntry.responsibleUser == 'DataMigration';
  }
}
